import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LegalList, LegalNotice, LegalP, LegalPage, LegalSection } from '../components/Legal/LegalPage';
import { DataDeletionRequestPayload, submitDataDeletionRequest } from '../services/publicApi';

const reasonOptions = [
  'Delete my account data',
  'Remove my contact details from a listing',
  'Correct inaccurate information',
  'Opt out of future ride lead display',
  'Report unauthorized listing',
  'Other',
];

const emptyForm: DataDeletionRequestPayload = {
  name: '',
  phoneNumber: '',
  email: '',
  reason: '',
  message: '',
};

const DataDeletion: React.FC = () => {
  const updated = 'May 14, 2026';
  const [form, setForm] = useState<DataDeletionRequestPayload>(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<{ requestId: string; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateField = (field: keyof DataDeletionRequestPayload) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await submitDataDeletionRequest(form);
      setSuccess({ requestId: response.requestId, message: response.message });
      setForm(emptyForm);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to submit your request right now. Please try again or contact support.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LegalPage
      title="Data Deletion / Opt-out"
      updated={updated}
      intro="Use this page to request correction, removal, masking, deletion, or opt-out for personal information or ride lead information associated with you."
    >
      <LegalNotice>
        RideSaathi only processes ride-related information from authorized sources and verified contributors. We review deletion, correction, removal, and opt-out requests so incorrect, expired, or unauthorized listings can be addressed.
      </LegalNotice>

      <LegalSection title="1. What you can request">
        <LegalList
          items={[
            'Correction of inaccurate account, contact, or ride-related information.',
            'Removal or masking of your contact details from a ride listing where applicable.',
            'Deletion of account data, support data, or ride lead information associated with you, subject to legal and operational retention needs.',
            'Opt-out from future display of your personal information in RideSaathi listings where applicable.',
            'Review of incorrect, expired, misleading, unauthorized, or non-ride listings.',
          ]}
        />
      </LegalSection>

      <LegalSection title="2. Submit a request">
        <Paper
          component="form"
          onSubmit={handleSubmit}
          variant="outlined"
          sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2, borderColor: 'rgba(7,24,45,0.1)' }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6" fontWeight={900}>Request form</Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Please provide enough detail for us to identify the relevant account, phone number, or ride listing.
              </Typography>
            </Box>

            {success && (
              <Alert severity="success">
                <Typography variant="subtitle2" fontWeight={800}>Request submitted</Typography>
                <Typography variant="body2">{success.message}</Typography>
                <Typography variant="caption" display="block" mt={0.75}>Reference ID: {success.requestId}</Typography>
              </Alert>
            )}

            {error && <Alert severity="error">{error}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Name"
                  value={form.name}
                  onChange={updateField('name')}
                  required
                  fullWidth
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone number"
                  value={form.phoneNumber}
                  onChange={updateField('phoneNumber')}
                  required
                  fullWidth
                  inputProps={{ maxLength: 24 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={form.email}
                  onChange={updateField('email')}
                  required
                  fullWidth
                  inputProps={{ maxLength: 160 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Reason for request"
                  value={form.reason}
                  onChange={updateField('reason')}
                  required
                  fullWidth
                >
                  {reasonOptions.map((reason) => (
                    <MenuItem key={reason} value={reason}>{reason}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Message/details"
                  value={form.message}
                  onChange={updateField('message')}
                  required
                  fullWidth
                  multiline
                  minRows={5}
                  inputProps={{ maxLength: 3000 }}
                  helperText="Include route, listing date, phone number, account email, or any other detail that helps us locate the data."
                />
              </Grid>
            </Grid>

            <Box>
              <Button type="submit" variant="contained" color="secondary" disabled={submitting} sx={{ fontWeight: 800 }}>
                {submitting ? 'Submitting...' : 'Submit request'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      </LegalSection>

      <LegalSection title="3. Review process">
        <LegalP>
          After submission, your request is stored with pending status for admin review. The status may later move to reviewed, completed, or rejected depending on verification and applicable retention requirements.
        </LegalP>
        <LegalP>
          We may contact you for additional verification before changing, masking, removing, or deleting data.
        </LegalP>
      </LegalSection>

      <LegalSection title="4. Platform and third-party disclaimer">
        <LegalP>
          RideSaathi is only a platform for organizing rides and does not guarantee ride booking, payment, safety, driver or customer verification, or ride completion.
        </LegalP>
        <LegalP>
          RideSaathi does not claim partnership, sponsorship, endorsement, or affiliation with WhatsApp, Meta, Google, Apple, or any third-party platform unless officially stated in writing by RideSaathi and the relevant third party.
        </LegalP>
      </LegalSection>
    </LegalPage>
  );
};

export default DataDeletion;
